import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AttachFileIcon,
  Box,
  ClaimscoreLogoLarge,
  ClaimscoreLogoSmall,
  ClipboardIcon,
  DashboardIcon,
  Flags,
  KeyOutlinedIcon,
  MenuOpenIcon,
  NavItem,
  SettingsIcon,
  Spacing,
  Stack,
  styled,
  SupervisorAccountIcon,
  useBreakpoints,
  useFlag,
  useTheme,
} from 'ui';
import { RoleLevel } from '@context/UserOrgCase.types';
import { useUserOrgCase } from '@context/UserOrgCaseContext';
import { getOrgAndCaseId } from '@utils/getOrgAndCaseId';
import { PATHS } from '@utils/paths';
import { UserRole } from 'services/ClaimscoreApiService/shared/types';
import { SIDENAVBAR_CONTENT_WRAPPER } from './SideNavbar.testIds';

const ListBox = styled(Box)`
  margin-top: ${({ theme }) => theme.spacing(Spacing.Large)};
`;
const LogoBox = styled(Box)`
  padding-left: ${({ theme }) => theme.spacing(Spacing.Small)};
`;
const SidebarBox = styled(Box)`
  display: flex;
  gap: ${({ theme }) => theme.spacing(Spacing.Medium)};
  margin-top: ${({ theme }) => theme.spacing(Spacing.Medium)};
`;

const StyledMenuOpenIcon = styled(MenuOpenIcon)`
  cursor: pointer;
`;

const SIDE_NAVBAR_ITEMS = [
  {
    text: 'Dashboard',
    icon: <DashboardIcon />,
    to: `/dashboard`,
  },
];

const ADMIN_ITEMS = [
  ...SIDE_NAVBAR_ITEMS,
  {
    text: 'Permissions',
    icon: <SupervisorAccountIcon />,
    to: `/permissions/organization`,
  },
];

const REPORTS_ITEM = {
  text: 'Reports',
  icon: <ClipboardIcon />,
  to: PATHS.PRIVATE_BASE.scheduledReports,
};

const ADMIN_ITEMS_WITH_REPORTS = [...ADMIN_ITEMS, REPORTS_ITEM];

const CASE_SETTINGS = {
  text: 'Case Settings',
  icon: <SettingsIcon />,
  to: `${PATHS.PRIVATE_BASE.caseSettings}/basic-information`,
};

const CLAIM_FILES = {
  text: 'Claim Files',
  icon: <AttachFileIcon />,
  to: `${PATHS.PRIVATE_BASE.claimFiles}`,
};

const DEVELOPER_TOOLS = {
  text: 'Developer Tools',
  icon: <KeyOutlinedIcon />,
  to: '/developer-tools/api-keys',
};

export interface MainSideNavbarContentProps {
  readonly open?: boolean;
  readonly onClose?: () => void;
}

export function MainSideNavbarContent({
  open,
  onClose,
}: MainSideNavbarContentProps) {
  const theme = useTheme();
  const { isMdUp } = useBreakpoints();
  const { isAdminOrOwnerInAnyOrgOrCase, getUserRole } = useUserOrgCase();
  const { search } = useLocation();
  const isScheduledReportsEnabled = useFlag(Flags.scheduleReports);
  const isCaseSettingsEnabled = useFlag(Flags.caseSettings);
  const isUploadClaimFilesEnabled = useFlag(Flags.uploadClaimsFile);
  const isDeveloperToolsEnabled = useFlag(Flags.developerTools);

  const isDeveloper = getUserRole(RoleLevel.case) === UserRole.Developer;

  const getSideNavbarBoxContent = useMemo(() => {
    if (open) {
      return (
        <LogoBox>
          <ClaimscoreLogoLarge
            aria-label="ClaimScore Logo"
            color={theme.palette.paper[200]}
          />
          {!isMdUp && (
            <StyledMenuOpenIcon aria-label="menu-open-icon" onClick={onClose} />
          )}
        </LogoBox>
      );
    }
    return (
      <LogoBox>
        <ClaimscoreLogoSmall
          aria-label="ClaimScore Logo"
          color={theme.palette.paper[200]}
        />
      </LogoBox>
    );
  }, [open, theme.palette.paper, isMdUp, onClose]);

  const getSideNavbarItems = useMemo(() => {
    let sideNavbarItems = SIDE_NAVBAR_ITEMS;

    if (isAdminOrOwnerInAnyOrgOrCase) {
      sideNavbarItems = ADMIN_ITEMS;
      if (isScheduledReportsEnabled && isCaseSettingsEnabled) {
        sideNavbarItems = [...ADMIN_ITEMS_WITH_REPORTS, CASE_SETTINGS];
        if (isUploadClaimFilesEnabled) {
          sideNavbarItems = [...sideNavbarItems, CLAIM_FILES];
        }
      }
    }

    if (
      isDeveloperToolsEnabled &&
      (isAdminOrOwnerInAnyOrgOrCase || isDeveloper)
    ) {
      sideNavbarItems = [...sideNavbarItems, DEVELOPER_TOOLS];
    }

    return sideNavbarItems.map(({ text, icon, to }) => (
      <NavItem
        icon={icon}
        key={text}
        kind="dark"
        label={text}
        to={`${to}${getOrgAndCaseId(search)}`}
      />
    ));
  }, [
    isAdminOrOwnerInAnyOrgOrCase,
    isCaseSettingsEnabled,
    isDeveloper,
    isDeveloperToolsEnabled,
    isScheduledReportsEnabled,
    isUploadClaimFilesEnabled,
    search,
  ]);

  return (
    <Stack data-testid={SIDENAVBAR_CONTENT_WRAPPER}>
      <SidebarBox>{getSideNavbarBoxContent}</SidebarBox>
      <ListBox>{getSideNavbarItems}</ListBox>
    </Stack>
  );
}
